import type { Characterization, CharacterizationPayment } from '../../types';

/**
 * P-07 (D-50, D-74, D-77) — pure helpers of the "אפיון" card (no React; unit
 * tested in utils/__tests__/characterizationModel.test.ts).
 */

/** "12:30" — hours:minutes of the reported time. */
export const hoursMinutes = (minutes: number | null | undefined): string => {
  const m = Math.max(0, Math.round(Number(minutes ?? 0)));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
};

/** The DOM id of a card (the overview line and the engagement banner link to it). */
export const characterizationAnchor = (id: string) => `char-${id}`;

/** The customer page link to a card (the "אפיונים והצעות" tab, scrolled to it). */
export const characterizationHref = (orgId: string, id: string) =>
  `/organizations/${orgId}?tab=quotes#${characterizationAnchor(id)}`;

/** 50% / 50% of a price before VAT; the second takes the rounding remainder (same as the server). */
export function installmentSplit(price: number | null | undefined): [number, number] | null {
  if (price === null || price === undefined || !Number.isFinite(Number(price)) || Number(price) < 0) return null;
  const p = Math.round(Number(price) * 100) / 100;
  const first = Math.round((p / 2) * 100) / 100;
  return [first, Math.round((p - first) * 100) / 100];
}

/** D-74 §7 — a warning (never a block) while the advance (installment 1) is not paid. */
export function advanceUnpaid(card: Pick<Characterization, 'status' | 'payments'>): boolean {
  if (card.status !== 'active') return false;
  return card.payments.some((p: CharacterizationPayment) => p.installment_index === 1 && p.status === 'pending');
}

/**
 * D-143 (QX-76) — "שולם" may be marked only after the OTE manager approved
 * the installment's charge. No charge (before P-14 / no price / deleted):
 * nothing to wait for. The server enforces the same rule (409).
 */
export function chargeAwaitsApproval(p: Pick<CharacterizationPayment, 'status' | 'charge_approval_status'>): boolean {
  if (p.status === 'paid') return false;
  const st = p.charge_approval_status;
  return !!st && st !== 'approved' && st !== 'sent_to_morning';
}

/**
 * D-262 (QX-1243) — "שולם" was cancelled but the installment's charge is still locked (the server does not clear the
 * lock by itself): the card shows "הכרטיס עדיין נעול. לשחרר?" with a manual release.
 */
export function chargeStillLocked(p: Pick<CharacterizationPayment, 'status' | 'charge_locked'>): boolean {
  return p.status === 'pending' && p.charge_locked === true;
}

/** "2/2" — how many installments are paid. */
export const paidCount = (payments: readonly Pick<CharacterizationPayment, 'status'>[]) =>
  payments.filter((p) => p.status === 'paid').length;

/** D-77 — the characterization quotes of the customer a new card may link to (not cancelled / superseded, no card yet). */
export function linkableCharacterizationQuotes<Q extends { id: string; quote_type: string; status: string }>(
  quotes: readonly Q[],
  cards: readonly Pick<Characterization, 'quote_id'>[],
): Q[] {
  const taken = new Set(cards.map((c) => c.quote_id).filter(Boolean));
  return quotes.filter((q) => q.quote_type === 'characterization' && q.status !== 'cancelled' && q.status !== 'superseded' && !taken.has(q.id));
}
